import { useCallback, useState } from 'react';

import { IconsEnum } from '../../../../../../../assets/icons/types';
import { Units } from '../../../../../../../types';

import { Popover } from '../../../../../../../helpers/Popover';
import { Translate } from '../../../../../../../helpers/Translate';
import { modelViewersKeys } from '../../../../../../../locales/keys';
import { PureTooltipIconRefButtonHelper } from '../../../../../../../helpers/buttons/PureTooltipIconRefButtonHelper';
import { useDropdownState } from '../../../../../../../helpers/dropdowns/hooks/useDropdownState';

import { PopoverPlacement } from '../../../../../../../helpers/Popover/popoverConstants';
import { TooltipPlacement } from '../../../../../../../helpers/tooltips/tooltipsConstants';

import { MeshEdgesMode } from '../../hooks/useModelViewerMeshEdges';
import {
  MODEL_VIEWER_ASPECT_RATIO_AUTO,
  ModelViewerAspectRatioSelection,
  ModelViewerImageSizeValue
} from '../../modelViewerAspectRatioConstants';

import { DisplaySettingsAspectRatioPanel } from './components/DisplaySettingsAspectRatioPanel';
import { DisplaySettingsMainPanel } from './components/DisplaySettingsMainPanel';
import { DisplaySettingsMeshPanel } from './components/DisplaySettingsMeshPanel';
import { DisplaySettingsResolutionPanel } from './components/DisplaySettingsResolutionPanel';
import { DisplaySettingsUnitsPanel } from './components/DisplaySettingsUnitsPanel';

type Panel = 'main' | 'units' | 'mesh' | 'aspect' | 'resolution';

interface ModelViewerDisplaySettingsButtonProps {
  buttonClassName?: string;
  withDimensions: boolean;
  onToggleDimensions: () => void;
  withGridAxesHelper: boolean;
  onToggleGridAxes: () => void;
  isNeutralMode: boolean;
  onToggleNeutral: () => void;
  units: Units;
  onSelectUnits: (units: Units) => void;
  meshEdgesMode: MeshEdgesMode;
  onSelectMesh: (mode: MeshEdgesMode) => void;
  aspectRatio: ModelViewerAspectRatioSelection;
  onSelectAspect: (value: ModelViewerAspectRatioSelection) => void;
  imageSize: ModelViewerImageSizeValue;
  onSelectImageSize: (value: ModelViewerImageSizeValue) => void;
  showGridLines: boolean;
  onToggleGridLines: () => void;
}

const meshLabels: Record<MeshEdgesMode, string> = {
  none: 'Normal',
  edges: 'Edges',
  wireframe: 'Wireframe'
};

function ModelViewerDisplaySettingsButton({
  buttonClassName,
  withDimensions,
  onToggleDimensions,
  withGridAxesHelper,
  onToggleGridAxes,
  isNeutralMode,
  onToggleNeutral,
  units,
  onSelectUnits,
  meshEdgesMode,
  onSelectMesh,
  aspectRatio,
  onSelectAspect,
  imageSize,
  onSelectImageSize,
  showGridLines,
  onToggleGridLines
}: ModelViewerDisplaySettingsButtonProps) {
  const [panel, setPanel] = useState<Panel>('main');

  const {
    isPopoverOpen,
    referencePopoverElement,
    referencePopoverCallbackRef,
    togglePopover,
    closePopover
  } = useDropdownState();

  const handleClose = useCallback(() => {
    setPanel('main');
    closePopover();
  }, [closePopover]);

  const handleToggle = useCallback(() => {
    if (!isPopoverOpen) {
      setPanel('main');
    }
    togglePopover();
  }, [isPopoverOpen, togglePopover]);

  const unitsLabel = units === Units.CM ? 'cm' : 'in';

  return (
    <div className="relative inline-flex">
      <PureTooltipIconRefButtonHelper
        ref={referencePopoverCallbackRef}
        icon={IconsEnum.ADJUSTMENTS_ALT_SOLID}
        iconClassName="h-5 w-5"
        className={
          buttonClassName ||
          'py-2 pl-2 pr-2 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-6 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0'
        }
        tooltipText="Display settings"
        tooltipPlacement={TooltipPlacement.TOP}
        onClick={handleToggle}
      />
      {isPopoverOpen && (
        <Popover
          referenceElement={referencePopoverElement}
          placement={PopoverPlacement.TOP_START}
          distanceOffset={6}
          className="min-w-48 lg:min-w-60 z-50 overflow-y-auto bg-white dark:bg-gray-700 dark:text-white border border-transparent focus:outline-none py-1 ring-1 ring-black ring-opacity-5 rounded-md shadow-lg text-gray-700 sm:max-w-80"
          closeOnOuterClick
          closePopover={handleClose}
        >
          {panel === 'main' && (
            <DisplaySettingsMainPanel
              withDimensions={withDimensions}
              onToggleDimensions={onToggleDimensions}
              withGridAxesHelper={withGridAxesHelper}
              onToggleGridAxes={onToggleGridAxes}
              isNeutralMode={isNeutralMode}
              onToggleNeutral={onToggleNeutral}
              unitsLabel={unitsLabel}
              meshLabel={meshLabels[meshEdgesMode]}
              aspectLabel={
                aspectRatio === MODEL_VIEWER_ASPECT_RATIO_AUTO ? (
                  <Translate id={modelViewersKeys.aspectRatioAuto} />
                ) : (
                  aspectRatio
                )
              }
              resolutionLabel={imageSize}
              onOpenUnits={() => setPanel('units')}
              onOpenMesh={() => setPanel('mesh')}
              onOpenAspect={() => setPanel('aspect')}
              onOpenResolution={() => setPanel('resolution')}
            />
          )}
          {panel === 'units' && (
            <DisplaySettingsUnitsPanel
              units={units}
              onBack={() => setPanel('main')}
              onSelect={onSelectUnits}
            />
          )}
          {panel === 'mesh' && (
            <DisplaySettingsMeshPanel
              meshEdgesMode={meshEdgesMode}
              onBack={() => setPanel('main')}
              onSelect={onSelectMesh}
            />
          )}
          {panel === 'aspect' && (
            <DisplaySettingsAspectRatioPanel
              aspectRatio={aspectRatio}
              onBack={() => setPanel('main')}
              onSelect={onSelectAspect}
              showGridLines={showGridLines}
              onToggleGridLines={onToggleGridLines}
            />
          )}
          {panel === 'resolution' && (
            <DisplaySettingsResolutionPanel
              imageSize={imageSize}
              onBack={() => setPanel('main')}
              onSelect={onSelectImageSize}
            />
          )}
        </Popover>
      )}
    </div>
  );
}

export default ModelViewerDisplaySettingsButton;
